import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { services } from '../../context';

export const Permissions = {
  BitwerxOfferingPluginManagerView: 'BitwerxOfferingPluginManagerView',
  BitwerxOfferingPluginManagerEdit: 'BitwerxOfferingPluginManagerEdit',
  ModifyBitwerxOfferings: 'ModifyBitwerxOfferings',
  PluginManagerEdit: 'PluginManagerEdit',
  BulkManifestUpdate: 'BulkManifestUpdate',
  AdvancedConfiguration: 'AdvancedConfiguration',
} as const;

export type PermissionKey = (typeof Permissions)[keyof typeof Permissions];

export interface PermissionsContextValue {
  permissions: string[];
  hasPermission: (permissionOrList: string | string[]) => boolean;
  refetch: () => void;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

function checkHasPermission(
  permissionsToCheck: string | string[],
  userPermissions: string[] = [],
): boolean {
  const singleValueCheck = (value: string) => userPermissions?.indexOf(value) > -1;
  if (typeof permissionsToCheck === 'string') {
    return singleValueCheck(permissionsToCheck);
  }
  return Object.values(permissionsToCheck).every(singleValueCheck);
}

function readPermissionsFromAuth(): string[] {
  try {
    const account = services.auth.GetAccount();
    const claims = account?.idTokenClaims as { permissions?: string[] } | undefined;
    return claims?.permissions ?? [];
  } catch {
    return [];
  }
}

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>(() => readPermissionsFromAuth());

  const refetch = useCallback(() => {
    setPermissions(readPermissionsFromAuth());
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const hasPermission = useCallback(
    (permissionOrList: string | string[]) => checkHasPermission(permissionOrList, permissions),
    [permissions],
  );

  const value = useMemo<PermissionsContextValue>(
    () => ({ permissions, hasPermission, refetch }),
    [permissions, hasPermission, refetch],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export function usePermissionsContext(): PermissionsContextValue {
  const context = useContext(PermissionsContext);
  if (context === null) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }
  return context;
}

export default PermissionsContext;
